import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '@/components/MobileLayout';
import TopBar from '@/components/TopBar';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Globe, Rss, LogOut, ChevronRight, Sliders, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState({ paragraphs: 4, tone: 'neutre et journalistique', show_sources: true });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings');
        setSettings(data);
      } finally { setLoaded(true); }
    })();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.patch('/settings', next);
      setSettings(data);
      toast.success('Réglages enregistrés');
    } catch (e) { toast.error('Échec de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const doLogout = () => { logout(); nav('/login'); };

  return (
    <MobileLayout>
      <TopBar />
      <div className="px-5 py-4 border-b border-slate-100">
        <h1 className="text-xl font-bold text-slate-900" data-testid="settings-title">Réglages</h1>
        <p className="text-sm text-slate-500">{user?.email}</p>
      </div>

      <div className="px-5 pt-5 pb-6 space-y-4">
        <button
          onClick={() => nav('/settings/sites')}
          data-testid="row-wp-sites"
          className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3 hover:shadow-md transition"
        >
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <Globe size={20} />
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold text-slate-900">Médias</div>
            <div className="text-sm text-slate-500">Sites WordPress & prompts éditoriaux</div>
          </div>
          <ChevronRight className="text-slate-400" size={20} />
        </button>

        <button
          onClick={() => nav('/settings/feeds')}
          data-testid="row-rss-feeds"
          className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3 hover:shadow-md transition"
        >
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <Rss size={20} />
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold text-slate-900">Flux RSS</div>
            <div className="text-sm text-slate-500">Sources d'actualités</div>
          </div>
          <ChevronRight className="text-slate-400" size={20} />
        </button>

        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="section-editorial">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Sliders size={20} />
            </div>
            <div className="font-semibold text-slate-900">Réglages éditoriaux</div>
          </div>

          {loaded && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                  Nombre de paragraphes
                </label>
                <div className="flex gap-2">
                  {[3, 4, 5, 6].map((n) => (
                    <button
                      key={n}
                      onClick={() => save({ ...settings, paragraphs: n })}
                      data-testid={`btn-paragraphs-${n}`}
                      className={`flex-1 py-2 rounded-xl font-semibold border-2 transition ${settings.paragraphs === n ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                  Ton éditorial
                </label>
                <input
                  type="text"
                  value={settings.tone}
                  onChange={(e) => setSettings({ ...settings, tone: e.target.value })}
                  onBlur={() => save(settings)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none"
                  data-testid="input-tone"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.show_sources}
                  onChange={(e) => save({ ...settings, show_sources: e.target.checked })}
                  className="w-5 h-5 accent-blue-600"
                  data-testid="check-sources"
                />
                <span className="text-sm font-medium text-slate-700">Afficher les sources en bas d'article</span>
              </label>

              {saving && <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Enregistrement…</div>}
            </div>
          )}
        </div>

        <button
          onClick={doLogout}
          data-testid="btn-logout"
          className="w-full bg-white border-2 border-red-200 text-red-600 font-semibold py-3 rounded-xl hover:bg-red-50 active:scale-95 transition flex items-center justify-center gap-2"
        >
          <LogOut size={18} /> Déconnexion
        </button>
      </div>
    </MobileLayout>
  );
}
